import { on, oncePromise, removeListener, run } from '../helper/electron';
import {
  AgentChatImageAttachment,
  AgentChatMessage,
  AgentModel,
  AgentModelParam,
  AgentModelSelection,
  AgentRun,
  AgentRunStatus,
  AgentRunUsage,
} from '../components/agentChat/AgentChat.types';
import { ChatEngine, isChatEngine } from '../components/agentChat/agentEngine';
import isElectron from '../utils/isElectron';
import { config } from '../config';
import { AppIdentifiers } from '../store/slices/token-state.slice';
import { getAppStore } from '../store/storeRef';
import { isChatTelemetryConfigured } from './ChatTelemetry.service';

/**
 * Renderer side of the dashboard agent chat. The agent itself runs in the main
 * process, so everything here is a thin pass-through over IPC.
 */

const CHANNELS = {
  start: 'agent/start',
  cancel: 'agent/cancel',
  listRuns: 'agent/list-runs',
  deleteRun: 'agent/delete-run',
  clearRuns: 'agent/clear-runs',
  status: 'agent/status',
  listModels: 'agent/list-models',
  message: 'agent/message',
  runUpdated: 'agent/run-updated',
  appendNote: 'agent/append-note',
  setChatLogs: 'agent/set-chat-logs',
  setUserToken: 'agent/set-user-token',
} as const;

/** Shape main sends over IPC, where the run is keyed by runId rather than id. */
interface AgentRunPayload {
  runId: string;
  prompt: string;
  status: AgentRunStatus;
  createdAt: string;
  messages: AgentChatMessage[];
  agentId?: string | null;
  error?: string | null;
  usage?: AgentRunUsage | null;
  costCents?: number | null;
  model?: string | null;
  modelParams?: AgentModelParam[] | null;
  engine?: ChatEngine | string | null;
}

const toRun = (payload: AgentRunPayload): AgentRun => ({
  id: payload.runId,
  prompt: payload.prompt,
  status: payload.status,
  createdAt: payload.createdAt,
  messages: payload.messages ?? [],
  agentId: payload.agentId ?? null,
  error: payload.error ?? null,
  usage: payload.usage ?? null,
  costCents: payload.costCents ?? null,
  model: payload.model ?? null,
  modelParams: payload.modelParams ?? null,
  engine: isChatEngine(payload.engine) ? payload.engine : 'direct',
});

export const isAgentChatAvailable = (): boolean => isElectron();

export type AgentChatStatus = {
  configured: boolean;
  cursorConfigured: boolean;
  directConfigured: boolean;
};

const emptyStatus = (): AgentChatStatus => ({
  configured: false,
  cursorConfigured: false,
  directConfigured: false,
});

/** Whether each chat engine has an API key in the main process. */
export const fetchAgentStatus = async (): Promise<AgentChatStatus> => {
  if (!isAgentChatAvailable()) return emptyStatus();
  const response = oncePromise<Partial<AgentChatStatus>>(CHANNELS.status);
  run(CHANNELS.status);
  const payload = await response;
  const cursorConfigured = !!payload.cursorConfigured;
  const directConfigured = !!payload.directConfigured;
  return {
    cursorConfigured,
    directConfigured,
    configured: payload.configured ?? directConfigured,
  };
};

export const fetchRuns = async (): Promise<AgentRun[]> => {
  if (!isAgentChatAvailable()) return [];
  const response = oncePromise<{ runs: AgentRunPayload[] }>(CHANNELS.listRuns);
  run(CHANNELS.listRuns);
  const { runs } = await response;
  return (runs ?? []).map(toRun);
};

const readAppAccessToken = (): string | null =>
  getAppStore().getState().tokenState[AppIdentifiers.App]?.accessToken ?? null;

export const startRun = (
  runId: string,
  prompt: string,
  model?: AgentModelSelection | null,
  engine?: ChatEngine,
  images?: AgentChatImageAttachment[],
  messageId?: string,
): void => {
  run(CHANNELS.start, {
    runId,
    prompt,
    model,
    engine,
    accessToken: readAppAccessToken(),
    ...(images?.length ? { images } : {}),
    // Main reuses this id for the stored user message, so the optimistic
    // bubble is updated in place rather than remounted (which flashes images).
    ...(messageId ? { messageId } : {}),
  });
};

/**
 * Request/reply that gives up instead of hanging. An older main process has no
 * handler for a newly added channel, and a promise that never settles would
 * stall whatever is waiting on it.
 */
const request = <T>(
  channel: string,
  fallback: T,
  timeoutMs = 8000,
): Promise<T> =>
  new Promise<T>((resolve) => {
    let settled = false;

    const finish = (payload: T) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      removeListener(channel, listener);
      resolve(payload);
    };

    const listener = (payload: T) => finish(payload ?? fallback);
    const timer = window.setTimeout(() => finish(fallback), timeoutMs);

    on(channel, listener);
    run(channel);
  });

/** Models the account can run, plus the default when none is chosen. */
export const fetchModels = async (): Promise<{
  models: AgentModel[];
  defaultModel: string;
}> => {
  const empty = { models: [] as AgentModel[], defaultModel: '' };
  if (!isAgentChatAvailable()) return empty;

  const payload = await request<{
    models?: AgentModel[];
    defaultModel?: string;
  }>(CHANNELS.listModels, empty);

  return {
    models: payload.models ?? [],
    defaultModel: payload.defaultModel ?? '',
  };
};

export const cancelRun = (runId: string): void => {
  run(CHANNELS.cancel, { runId });
};

export type AgentChatNote = { role: 'user' | 'agent'; content: string };

/**
 * Appends app-made messages to a conversation — used by the song list card so
 * a button click and its outcome land at the bottom of the thread like any
 * other turn. Main persists them and replies over the run-updated channel.
 */
export const appendRunNotes = (runId: string, notes: AgentChatNote[]): void => {
  if (!isAgentChatAvailable() || !notes.length) return;
  run(CHANNELS.appendNote, { runId, notes });
};

export const deleteRun = async (runId: string): Promise<AgentRun[]> => {
  const response = oncePromise<{ runs: AgentRunPayload[] }>(CHANNELS.listRuns);
  run(CHANNELS.deleteRun, { runId });
  const { runs } = await response;
  return (runs ?? []).map(toRun);
};

export const clearAllRuns = async (): Promise<AgentRun[]> => {
  const response = oncePromise<{ runs: AgentRunPayload[] }>(CHANNELS.listRuns);
  run(CHANNELS.clearRuns);
  const { runs } = await response;
  return (runs ?? []).map(toRun);
};

export const subscribeToMessages = (
  listener: (runId: string, message: AgentChatMessage) => void,
): (() => void) => {
  const handler = (payload: { runId: string; message: AgentChatMessage }) => {
    if (payload?.runId && payload.message) {
      listener(payload.runId, payload.message);
    }
  };
  on(CHANNELS.message, handler);
  return () => removeListener(CHANNELS.message, handler);
};

export const subscribeToRunUpdates = (
  listener: (updated: AgentRun) => void,
): (() => void) => {
  const handler = (payload: { run: AgentRunPayload }) => {
    if (payload?.run) listener(toRun(payload.run));
  };
  on(CHANNELS.runUpdated, handler);
  return () => removeListener(CHANNELS.runUpdated, handler);
};

/** Gives main the Supabase credentials so it can pull live chat tuning. */
export const syncChatRemoteCredentials = (): void => {
  if (!isAgentChatAvailable()) return;
  if (isChatTelemetryConfigured()) {
    run(CHANNELS.setChatLogs, {
      url: config.CHAT_LOGS_URL,
      key: config.CHAT_LOGS_KEY,
    });
  }
  // Same Bearer token fetchWrapper sends to api.cratehackers.com (not Supabase Auth).
  run(CHANNELS.setUserToken, { accessToken: readAppAccessToken() });
};
